<template>
  <div class="relative min-w-0">
    <!-- Floating score ring, off-axis for interest -->
    <div class="absolute -top-4 right-1 sm:-top-6 sm:-right-4 z-20 w-20 h-20 sm:w-24 sm:h-24 card p-0 shadow-hero animate-float-y" aria-hidden="true">
      <div class="relative w-full h-full flex items-center justify-center">
        <svg class="absolute inset-0" viewBox="0 0 36 36">
          <path class="text-ink-100" stroke="currentColor" stroke-width="3" fill="none"
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"/>
          <path class="text-accent-500" stroke="currentColor" stroke-width="3" fill="none" stroke-linecap="round"
            stroke-dasharray="87, 100"
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"/>
        </svg>
        <div class="text-center">
          <div class="font-display text-2xl font-semibold text-ink-900 leading-none">87</div>
          <div class="text-2xs text-ink-500 mt-0.5">SEO score</div>
        </div>
      </div>
    </div>

    <!-- Main card -->
    <div class="card shadow-hero overflow-hidden">
      <!-- Card header: fake browser -->
      <div class="flex items-center gap-2 px-4 py-3 border-b border-ink-100 bg-ink-50/60">
        <div class="flex gap-1.5">
          <span class="w-2.5 h-2.5 rounded-full bg-ink-200"></span>
          <span class="w-2.5 h-2.5 rounded-full bg-ink-200"></span>
          <span class="w-2.5 h-2.5 rounded-full bg-ink-200"></span>
        </div>
        <div class="flex-1 min-w-0 bg-white border border-ink-200 rounded-md px-3 py-1 flex items-center gap-2 text-xs text-ink-600">
          <Icon name="lock" :size="12" classes="text-accent-600" />
          <span class="font-mono truncate min-w-0">leadranks.io/report/yourdomain.com</span>
        </div>
        <span class="badge-accent">
          <span class="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse"></span>
          Live
        </span>
      </div>

      <!-- Content -->
      <div class="p-5 space-y-4">
        <!-- Top summary -->
        <div class="flex items-center justify-between">
          <div>
            <div class="text-xs text-ink-500">Analyzing</div>
            <div class="font-mono text-sm text-ink-900 font-medium truncate">yourdomain.com</div>
          </div>
          <div class="flex items-center gap-1.5 text-xs text-ink-600">
            <Icon name="clock" :size="12" />
            <span>2m 47s</span>
          </div>
        </div>

        <!-- Progress bar -->
        <div>
          <div class="flex items-center justify-between text-xs mb-1.5">
            <span class="text-ink-600 font-medium">Running Lighthouse audit</span>
            <span class="font-mono text-ink-900 font-semibold">78%</span>
          </div>
          <div class="h-1.5 rounded-full bg-ink-100 overflow-hidden">
            <!-- Real bug fixed 2026-09-07: this animated the `width`
                 property directly (72%->82%->78%, infinite) -- Lighthouse's
                 "Avoid non-composited animations" audit flagged exactly
                 this element, and it's the diagnosed cause of a 0.484
                 desktop CLS on the homepage (width changes force a layout
                 recompute every frame; the GPU compositor can't handle
                 them the way it handles transform/opacity). Now animates
                 `transform: scaleX()` at a fixed 100% base width instead --
                 same visual pulse, compositor-only. transform-origin:left
                 so it still grows from the left edge, matching how a
                 real progress bar reads. -->
            <div class="h-full w-full bg-gradient-to-r from-brand-500 to-brand-700 rounded-full animate-pulse-width" style="transform-origin: left; transform: scaleX(0.78)"></div>
          </div>
        </div>

        <!-- Metrics row -->
        <div class="grid grid-cols-4 gap-2 pt-1">
          <MiniMetric label="LCP"    value="1.8s"  tone="accent" />
          <MiniMetric label="CLS"    value="0.04"  tone="accent" />
          <MiniMetric label="TBT"    value="240"   tone="warn"   />
          <MiniMetric label="FCP"    value="1.2s"  tone="accent" />
        </div>

        <!-- Issue list -->
        <div class="space-y-1.5 pt-2 border-t border-ink-100">
          <div class="text-2xs font-semibold uppercase tracking-wider text-ink-500 mb-2">Top findings</div>
          <IssueRow
            v-for="(item, i) in findings"
            :key="item.title"
            :icon="item.icon"
            :title="item.title"
            :impact="item.impact"
            :severity="item.severity"
            :style="{ animationDelay: `${500 + i * 120}ms` }"
            class="animate-fade-in opacity-0"
          />
        </div>
      </div>
    </div>

    <!-- Floating competitor chip -->
    <div class="hidden md:block absolute -bottom-4 -left-6 z-20 card shadow-float px-3 py-2 animate-float-y" style="animation-delay: 500ms">
      <div class="flex items-center gap-2">
        <div class="w-7 h-7 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-white font-semibold text-xs flex items-center justify-center">+12</div>
        <div class="text-xs">
          <div class="font-medium text-ink-900">Ranking up</div>
          <div class="text-ink-500">vs last scan</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import Icon from '../ui/Icon.vue';
import MiniMetric from './MiniMetric.vue';
import IssueRow from './IssueRow.vue';

const findings = [
  { icon: 'check',         title: 'All 42 meta descriptions present', impact: 'Healthy',  severity: 'good' },
  { icon: 'alert-triangle',title: '3 images missing alt text',        impact: 'Fix soon', severity: 'warn' },
  { icon: 'alert-triangle',title: 'LCP exceeds 2.5s on mobile',       impact: 'High',     severity: 'danger' },
];
</script>
